import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authedLayoutRoute } from './authed.js';
import { createPayrollRun } from '../api/payrollRuns.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';

/**
 * /payroll-runs/new — create a draft run. Server assembles the snapshot,
 * pins the rule-spec hash, returns the run in status=draft. Calculate is
 * a separate action on the detail page.
 *
 * supersedesId is optional — the correction flow (v2 supersedes v1) ships
 * a "Correct run" button on the detail page of approved runs. That path
 * pre-fills this form with supersedesId + the prior run's period dates.
 */

const RunForm = z
  .object({
    periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    payDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    supersedesId: z.string().optional(),
  })
  .refine((v) => v.periodStart <= v.periodEnd, {
    message: 'periodStart must be on or before periodEnd',
    path: ['periodStart'],
  })
  .refine((v) => v.payDate >= v.periodEnd, {
    message: 'payDate must be on or after periodEnd',
    path: ['payDate'],
  });
type RunForm = z.infer<typeof RunForm>;

export const payrollRunNewRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/payroll-runs/new',
  validateSearch: (search: Record<string, unknown>): { supersedesId?: string } => ({
    supersedesId:
      typeof search.supersedesId === 'string' ? search.supersedesId : undefined,
  }),
  component: PayrollRunNewPage,
});

function PayrollRunNewPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { supersedesId: supersedesFromSearch } = payrollRunNewRoute.useSearch();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RunForm>({
    resolver: zodResolver(RunForm),
    defaultValues: {
      periodStart: '',
      periodEnd: '',
      payDate: '',
      supersedesId: supersedesFromSearch ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: createPayrollRun,
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['payroll-runs', 'list'] });
      await navigate({ to: '/payroll-runs/$id', params: { id: created.id } });
    },
  });

  function onSubmit(values: RunForm): void {
    mutation.mutate({
      periodStart: values.periodStart,
      periodEnd: values.periodEnd,
      payDate: values.payDate,
      ...(values.supersedesId ? { supersedesId: values.supersedesId } : {}),
    });
  }

  return (
    <section className="p-8 space-y-6 max-w-2xl">
      <header className="space-y-2">
        <nav className="text-xs text-gray-500">
          <Link to="/payroll-runs" className="hover:underline">
            Payroll runs
          </Link>{' '}
          / New
        </nav>
        <h1 className="font-serif text-3xl">New payroll run</h1>
        <p className="text-sm text-gray-400">
          The server assembles the full snapshot (employees, rule specs, FX
          rates) and pins the combined rule-spec hash. After creating, the
          draft is ready to calculate.
        </p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DateField
            id="periodStart"
            label="Period start *"
            error={errors.periodStart?.message}
            inputProps={register('periodStart')}
          />
          <DateField
            id="periodEnd"
            label="Period end *"
            error={errors.periodEnd?.message}
            inputProps={register('periodEnd')}
          />
          <DateField
            id="payDate"
            label="Pay date *"
            error={errors.payDate?.message}
            inputProps={register('payDate')}
          />
        </div>

        {supersedesFromSearch ? (
          <div className="rounded border border-gray-800 bg-gray-950 p-4 space-y-1">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Correction of
            </p>
            <p className="font-mono text-xs text-gray-300">
              {supersedesFromSearch}
            </p>
            <p className="text-xs text-gray-500">
              Version auto-bumps to the superseded run&apos;s version + 1.
              Approving this run will auto-void the superseded one.
            </p>
          </div>
        ) : null}

        <ApiErrorNotice error={mutation.error} fallback="Something went wrong. Try again." />

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={isSubmitting || mutation.isPending}
            className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {isSubmitting || mutation.isPending ? 'Creating...' : 'Create draft run'}
          </button>
          <Link
            to="/payroll-runs"
            className="rounded border border-gray-700 px-4 py-2 text-sm hover:bg-gray-900"
          >
            Cancel
          </Link>
        </div>
      </form>
    </section>
  );
}

interface DateFieldProps {
  id: string;
  label: string;
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<RunForm>>['register']>;
}

function DateField({ id, label, error, inputProps }: DateFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type="date"
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
